import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../../theme';
import DashboardLayout from '../../../../components/dashboard/DashboardLayout';
import { Card, CardHeader, CardTitle, CardBody } from '../../../../components/ui/Card';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { Table } from '../../../../components/ui/Table';
import { Modal } from '../../../../components/ui/Modal';
import { useAuth } from '../../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FiSearch, FiEye, FiEdit, FiX, FiPlus } from 'react-icons/fi';
import {
  fetchIndividualClients,
  fetchLocations,
  updateIndividualClient,
  createIndividualClient,
  formatDate,
  IndividualClientData,
  LocationData,
} from '../service/individualClientService';
import { auth } from '../../../../firebase/config';
import { Select } from '../../../../components/ui/Select';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const SearchInputContainer = styled.div`
  flex: 1;
  min-width: 250px;
`;

const SearchByContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  align-items: center;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  align-items: center;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  cursor: pointer;
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing[4]};
`;

const Badge = styled.span`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${theme.colors.light.background.tertiary};
  color: ${theme.colors.light.text.secondary};
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.colors.light.border.light};
  border-top: 4px solid ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto ${theme.spacing[4]};

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.light.text.secondary};
`;

const EmptyStateText = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.secondary};
  margin: 0;
`;

const IndividualClientPage: React.FC = () => {
  const { user, companyData, staffData } = useAuth();
  const [clients, setClients] = useState<IndividualClientData[]>([]);
  const [filtered, setFiltered] = useState<IndividualClientData[]>([]);
  const [locations, setLocations] = useState<LocationData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchBy, setSearchBy] = useState<'name' | 'mobile'>('name');
  const [selectedClient, setSelectedClient] = useState<IndividualClientData | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check permissions
  const isStaffUser = !!staffData;
  const canViewClients = isStaffUser
    ? (staffData?.access?.clients === true)
    : !!companyData; // Admin/company owner can always view

  const canAddClients = isStaffUser
    ? (staffData?.access?.clients === true && (staffData?.permissions?.clients === 'add' || staffData?.permissions?.clients === 'edit' || staffData?.permissions?.clients === 'delete'))
    : !!companyData; // Admin/company owner can always add

  const canEditClients = isStaffUser
    ? (staffData?.access?.clients === true && (staffData?.permissions?.clients === 'edit' || staffData?.permissions?.clients === 'delete'))
    : !!companyData; // Admin/company owner can always edit

  // Create form state
  const [createFormData, setCreateFormData] = useState({
    clientName: '',
    mobile: '',
    address: '',
    locationId: '',
    dateOfRegistration: null as Date | null,
    requiredGuard: 1,
    contactName: '',
    contactEmail: '',
    contactNumber: '',
    salary: 0,
  });

  // Use company ID from context if available, otherwise fall back to authenticated user UID
  // The data is stored under: Individual/{companyId}/clients
  const companyId = companyData?.id || auth.currentUser?.uid || '';

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, companyData]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [clientsData, locationsData] = await Promise.all([
        fetchIndividualClients(companyId),
        fetchLocations(),
      ]);
      setClients(clientsData);
      setFiltered(clientsData);
      setLocations(locationsData);
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast.error(error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setFiltered(clients);
      return;
    }
    const results = clients.filter((c) =>
      c[searchBy]?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFiltered(results);
  };

  const handleReset = () => {
    setSearchTerm('');
    setSearchBy('name');
    setFiltered(clients);
  };

  const handleRadioChange = (value: 'name' | 'mobile') => {
    setSearchBy(value);
    setSearchTerm('');
    setFiltered(clients);
  };

  const handleFieldChange = (field: keyof IndividualClientData, value: any) => {
    setSelectedClient((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const handleUpdate = async () => {
    if (!selectedClient?.id) {
      toast.error('Invalid client');
      return;
    }

    setLoading(true);
    try {
      await updateIndividualClient(selectedClient.id, selectedClient, companyId);
      toast.success('Client updated successfully');
      await loadData();
      setIsEditing(false);
      setShowModal(false);
      setSelectedClient(null);
    } catch (error: any) {
      console.error('Update error:', error);
      toast.error(error.message || 'Failed to update client');
    } finally {
      setLoading(false);
    }
  };

  const getLocationName = (locationId?: string) => {
    if (!locationId) return 'N/A';
    const loc = locations.find((l) => l.id === locationId);
    return loc ? loc.name : 'N/A';
  };

  const handleCreateClient = async () => {
    if (!createFormData.clientName.trim()) {
      toast.error('Client name is required');
      return;
    }
    if (!createFormData.locationId) {
      toast.error('Location is required');
      return;
    }

    setLoading(true);
    try {
      await createIndividualClient(
        createFormData.clientName,
        createFormData.mobile,
        createFormData.address,
        createFormData.locationId,
        createFormData.dateOfRegistration,
        companyId,
        createFormData.requiredGuard,
        createFormData.contactName,
        createFormData.contactEmail,
        createFormData.contactNumber,
        createFormData.salary
      );
      toast.success('Individual client created successfully!');
      setShowCreateModal(false);
      setCreateFormData({
        clientName: '',
        mobile: '',
        address: '',
        locationId: '',
        dateOfRegistration: null,
        requiredGuard: 1,
        contactName: '',
        contactEmail: '',
        contactNumber: '',
        salary: 0,
      });
      await loadData();
    } catch (error: any) {
      console.error('Create error:', error);
      toast.error(error.message || 'Failed to create client');
    } finally {
      setLoading(false);
    }
  };

  // Don't render if user doesn't have access
  if (!canViewClients) {
    return (
      <DashboardLayout>
        <Card>
          <CardBody>
            <EmptyState>
              <EmptyStateText>You don't have permission to view individual clients.</EmptyStateText>
            </EmptyState>
          </CardBody>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Individual Clients</PageTitle>
        {canAddClients && (
          <Button onClick={() => setShowCreateModal(true)}>
            <FiPlus size={18} />
            Add Individual Client
          </Button>
        )}
      </PageHeader>

      <SearchContainer>
        <SearchInputContainer>
          <Input
            placeholder={`Search by ${searchBy}`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            dark
          />
        </SearchInputContainer>
        <SearchByContainer>
          <RadioGroup>
            <RadioLabel>
              <input
                type="radio"
                value="name"
                checked={searchBy === 'name'}
                onChange={() => handleRadioChange('name')}
              />
              Name
            </RadioLabel>
            <RadioLabel>
              <input
                type="radio"
                value="mobile"
                checked={searchBy === 'mobile'}
                onChange={() => handleRadioChange('mobile')}
              />
              Mobile
            </RadioLabel>
          </RadioGroup>
        </SearchByContainer>
        <Button onClick={handleSearch}>
          <FiSearch size={18} />
          Search
        </Button>
        {searchTerm && (
          <Button variant="secondary" onClick={handleReset}>
            <FiX size={18} />
            Reset
          </Button>
        )}
      </SearchContainer>

      {loading && clients.length === 0 ? (
        <LoadingContainer>
          <Spinner />
          <p style={{ color: theme.colors.light.text.secondary }}>Loading clients...</p>
        </LoadingContainer>
      ) : filtered.length === 0 ? (
        <EmptyState>
          <p>No clients found</p>
        </EmptyState>
      ) : (
        <Card>
          <CardBody>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Name</Table.HeaderCell>
                  <Table.HeaderCell>Mobile</Table.HeaderCell>
                  <Table.HeaderCell>Address</Table.HeaderCell>
                  <Table.HeaderCell>Location</Table.HeaderCell>
                  <Table.HeaderCell>Date of Registration</Table.HeaderCell>
                  <Table.HeaderCell>Client Code</Table.HeaderCell>
                  <Table.HeaderCell>Action</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filtered.map((client) => (
                  <Table.Row key={client.id}>
                    <Table.Cell>{client.name}</Table.Cell>
                    <Table.Cell>{client.mobile || 'N/A'}</Table.Cell>
                    <Table.Cell>{client.address || 'N/A'}</Table.Cell>
                    <Table.Cell>{getLocationName(client.locationId)}</Table.Cell>
                    <Table.Cell>
                      {client.dateOfRegistration
                        ? formatDate(client.dateOfRegistration)
                        : 'N/A'}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge>{client.securityCode || 'N/A'}</Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Button size="sm" variant="primary" onClick={() => {
                        setSelectedClient({ ...client });
                        setIsEditing(false);
                        setShowModal(true);
                      }}>
                        <FiEye size={18} />
                        View
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </CardBody>
        </Card>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={selectedClient?.name || 'Client Details'}
        footer={
          <>
            {!isEditing ? (
              <>
                {canEditClients && (
                  <Button variant="primary" onClick={() => setIsEditing(true)}>
                    <FiEdit size={18} />
                    Edit
                  </Button>
                )}
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Close
                </Button>
              </>
            ) : (
              <>
                <Button variant="primary" onClick={handleUpdate} disabled={loading}>
                  Save
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    const original = clients.find((c) => c.id === selectedClient?.id);
                    if (original) {
                      setSelectedClient(original);
                    }
                    setIsEditing(false);
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          </>
        }
      >
        {selectedClient && (
          <div>
            <FormGroup>
              <Input
                label="Name"
                value={selectedClient.name || ''}
                readOnly={!isEditing}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                dark
                fullWidth
                required
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Mobile"
                value={selectedClient.mobile || ''}
                readOnly={!isEditing}
                onChange={(e) => handleFieldChange('mobile', e.target.value)}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Address"
                value={selectedClient.address || ''}
                readOnly={!isEditing}
                onChange={(e) => handleFieldChange('address', e.target.value)}
                dark
                fullWidth
              />
            </FormGroup>

            <FormGroup>
              <Input
                label="Contact Name"
                value={selectedClient.contactName || ''}
                readOnly={!isEditing}
                onChange={(e) => handleFieldChange('contactName', e.target.value)}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Contact Email"
                value={selectedClient.contactEmail || ''}
                readOnly={!isEditing}
                onChange={(e) => handleFieldChange('contactEmail', e.target.value)}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Contact Number"
                value={selectedClient.contactNumber || ''}
                readOnly={!isEditing}
                onChange={(e) => handleFieldChange('contactNumber', e.target.value)}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Select
                label="Location"
                value={selectedClient.locationId || ''}
                onChange={(e) => handleFieldChange('locationId', e.target.value)}
                dark
                fullWidth
                disabled={!isEditing}
              >
                <option value="">Select Location</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Input
                label="Date of Registration"
                type="date"
                value={
                  selectedClient.dateOfRegistration
                    ? selectedClient.dateOfRegistration instanceof Date
                      ? selectedClient.dateOfRegistration.toISOString().split('T')[0]
                      : new Date(selectedClient.dateOfRegistration).toISOString().split('T')[0]
                    : ''
                }
                onChange={(e) => {
                  if (isEditing) {
                    handleFieldChange('dateOfRegistration', e.target.value ? new Date(e.target.value) : null);
                  }
                }}
                readOnly={!isEditing}
                disabled={!isEditing}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Required Guards"
                type="number"
                value={String(selectedClient.requiredGuard || 1)}
                readOnly={!isEditing}
                onChange={(e) => handleFieldChange('requiredGuard', parseInt(e.target.value) || 1)}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Salary (Rs.)"
                type="number"
                value={String(selectedClient.salary || 0)}
                readOnly={!isEditing}
                onChange={(e) => handleFieldChange('salary', parseFloat(e.target.value) || 0)}
                dark
                fullWidth
              />
            </FormGroup>
          </div>
        )}
      </Modal>

      {/* Create Individual Client Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add Individual Client"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateClient} disabled={loading}>
              {loading ? 'Creating...' : 'Create Client'}
            </Button>
          </>
        }
      >
        <FormGroup>
          <Input
            label="Client Name *"
            value={createFormData.clientName}
            onChange={(e) => setCreateFormData({ ...createFormData, clientName: e.target.value })}
            placeholder="Enter client name"
            required
            fullWidth
            dark
          />
        </FormGroup>
        <FormGroup>
          <Input
            label="Mobile"
            value={createFormData.mobile}
            onChange={(e) => setCreateFormData({ ...createFormData, mobile: e.target.value })}
            placeholder="03xx-xxxxxxx"
            fullWidth
            dark
          />
        </FormGroup>
        <FormGroup>
          <Input
            label="Address"
            value={createFormData.address}
            onChange={(e) => setCreateFormData({ ...createFormData, address: e.target.value })}
            placeholder="Full address"
            fullWidth
            dark
          />
        </FormGroup>

        <FormGroup>
          <Input
            label="Contact Name"
            value={createFormData.contactName}
            onChange={(e) => setCreateFormData({ ...createFormData, contactName: e.target.value })}
            placeholder="Enter contact person name"
            fullWidth
            dark
          />
        </FormGroup>
        <FormGroup>
          <Input
            label="Contact Email"
            value={createFormData.contactEmail}
            onChange={(e) => setCreateFormData({ ...createFormData, contactEmail: e.target.value })}
            placeholder="contact@example.com"
            fullWidth
            dark
          />
        </FormGroup>
        <FormGroup>
          <Input
            label="Contact Number"
            value={createFormData.contactNumber}
            onChange={(e) => setCreateFormData({ ...createFormData, contactNumber: e.target.value })}
            placeholder="03xx-xxxxxxx"
            fullWidth
            dark
          />
        </FormGroup>
        <FormGroup>
          <Input
            label="Date of Registration"
            type="date"
            value={
              createFormData.dateOfRegistration
                ? createFormData.dateOfRegistration.toISOString().split('T')[0]
                : ''
            }
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCreateFormData({
                ...createFormData,
                dateOfRegistration: e.target.value ? new Date(e.target.value) : null,
              })
            }
            dark
            fullWidth
          />
        </FormGroup>
        <FormGroup>
          <Input
            label="Required Guards"
            type="number"
            value={String(createFormData.requiredGuard)}
            onChange={(e) => setCreateFormData({ ...createFormData, requiredGuard: parseInt(e.target.value) || 1 })}
            placeholder="Number of guards"
            fullWidth
            dark
          />
        </FormGroup>
        <FormGroup>
          <Input
            label="Client Salary (Rs.)"
            type="number"
            value={String(createFormData.salary)}
            onChange={(e) => setCreateFormData({ ...createFormData, salary: parseFloat(e.target.value) || 0 })}
            placeholder="Salary for guards"
            fullWidth
            dark
          />
        </FormGroup>
        <FormGroup>
          <Select
            label="Location *"
            value={createFormData.locationId}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCreateFormData({ ...createFormData, locationId: e.target.value })}
            dark
            fullWidth
            required
          >
            <option value="">Select Location</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </Select>
        </FormGroup>
      </Modal>
    </DashboardLayout>
  );
};

export default IndividualClientPage;

